/**
 * Amazon Ads — DSP Mashup Collection-Ad Column Map (Story 19.16.1, Task 8)
 *
 * 25 metrics for the Mashup creative format (multi-product carousel,
 * common in beauty + fashion DSP). Sourced from Bright Analytics V3 +
 * Funnel.io connector schema.
 *
 * Sub-categories per AC #7:
 *   - Core (5): mashupImpressions / mashupClicks / mashupCTR / mashupCost / mashupCPM
 *   - Per-product engagement (10)
 *   - Carousel-level engagement (5)
 *   - Brand-halo from mashup (5)
 *
 * All entries live within DSP_CAMPAIGN report-type because Amazon emits
 * mashup metrics within the standard programmatic reporting surface.
 */

import type { DspMetricColumnEntry } from "./dsp-column-map";

const KPI_LINE_BAR_TABLE = ["kpi", "line-chart", "bar-chart", "data-table"] as const;
const KPI_LINE_TABLE = ["kpi", "line-chart", "data-table"] as const;
const KPI_GAUGE_LINE = ["kpi", "gauge", "line-chart"] as const;

/** AC #7 — 25 mashup-family metric column-map entries. */
export const DSP_MASHUP_COLUMN_MAP: Record<string, DspMetricColumnEntry> = {
  // ===== Core (5) =====
  mashupImpressions: {
    key: "mashupImpressions",
    label: "Mashup Impressions",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupImpressions",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mashupClicks: {
    key: "mashupClicks",
    label: "Mashup Clicks",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mashupCTR: {
    key: "mashupCTR",
    label: "Mashup CTR",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupCTR",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "mashupClicks", denominator: "mashupImpressions" },
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  mashupCost: {
    key: "mashupCost",
    label: "Mashup Cost",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupCost",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  mashupCPM: {
    key: "mashupCPM",
    label: "Mashup CPM",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupCPM",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "mashupCost", denominator: "mashupImpressions" },
    compatibleWidgets: [...KPI_LINE_TABLE],
  },

  // ===== Per-product engagement (10) =====
  mashupProductHover: {
    key: "mashupProductHover",
    label: "Mashup Product Hover",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupProductHover",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mashupProductClicks: {
    key: "mashupProductClicks",
    label: "Mashup Product Clicks",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupProductClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mashupProductCTR: {
    key: "mashupProductCTR",
    label: "Mashup Product CTR",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupProductCTR",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "mashupProductClicks", denominator: "mashupImpressions" },
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  mashupProductDPV14d: {
    key: "mashupProductDPV14d",
    label: "Mashup Product DPV (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupProductDPV14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mashupProductDPVR14d: {
    key: "mashupProductDPVR14d",
    label: "Mashup Product DPVR (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupProductDPVR14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "mashupProductDPV14d", denominator: "mashupImpressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  mashupProductATC14d: {
    key: "mashupProductATC14d",
    label: "Mashup Product ATC (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupProductATC14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mashupProductATCR14d: {
    key: "mashupProductATCR14d",
    label: "Mashup Product ATCR (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupProductATCR14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "mashupProductATC14d", denominator: "mashupImpressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  mashupProductPurchases14d: {
    key: "mashupProductPurchases14d",
    label: "Mashup Product Purchases (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupProductPurchases14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mashupProductSales14d: {
    key: "mashupProductSales14d",
    label: "Mashup Product Sales (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupProductSales14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  mashupProductROAS14d: {
    key: "mashupProductROAS14d",
    label: "Mashup Product ROAS (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupProductROAS14d",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { numerator: "mashupProductSales14d", denominator: "mashupCost" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },

  // ===== Carousel-level engagement (5) =====
  mashupCarouselSwipes: {
    key: "mashupCarouselSwipes",
    label: "Mashup Carousel Swipes",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupCarouselSwipes",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mashupCarouselFullView: {
    key: "mashupCarouselFullView",
    label: "Mashup Carousel Full-View",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupCarouselFullView",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mashupCarouselSwipeRate: {
    key: "mashupCarouselSwipeRate",
    label: "Mashup Carousel Swipe Rate",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupCarouselSwipeRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "mashupCarouselSwipes", denominator: "mashupImpressions" },
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  mashupCarouselTimeSpent: {
    key: "mashupCarouselTimeSpent",
    label: "Mashup Carousel Time Spent",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupCarouselTimeSpent",
    dataType: "Decimal",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  mashupCarouselExits: {
    key: "mashupCarouselExits",
    label: "Mashup Carousel Exits",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupCarouselExits",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },

  // ===== Brand-halo from mashup (5) =====
  mashupBrandHaloDPV14d: {
    key: "mashupBrandHaloDPV14d",
    label: "Mashup Brand Halo DPV (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupBrandHaloDPV14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mashupBrandHaloATC14d: {
    key: "mashupBrandHaloATC14d",
    label: "Mashup Brand Halo ATC (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupBrandHaloATC14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mashupBrandHaloPurchases14d: {
    key: "mashupBrandHaloPurchases14d",
    label: "Mashup Brand Halo Purchases (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupBrandHaloPurchases14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mashupBrandHaloSales14d: {
    key: "mashupBrandHaloSales14d",
    label: "Mashup Brand Halo Sales (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupBrandHaloSales14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  mashupBrandHaloROAS14d: {
    key: "mashupBrandHaloROAS14d",
    label: "Mashup Brand Halo ROAS (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mashupBrandHaloROAS14d",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { numerator: "mashupBrandHaloSales14d", denominator: "mashupCost" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
};

export const DSP_MASHUP_COLUMN_COUNT = 25;
